import { Component, forwardRef, OnInit } from '@angular/core';
import { BasefieldV2Component } from '@app/formly/types/v2/basefield-v2/basefield-v2.component';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import IMask from 'imask';
import lpn from 'google-libphonenumber';
import { Country } from '@app/formly/types/tel-input/country.model';
import { CountryCode } from '../../../../../../static/country-code';

export const DEFAULT_COUNTRY = {
  areaCodes: null,
  dialCode: '',
  htmlId: '',
  flagClass: '',
  iso2: '',
  name: '',
  placeHolder: '',
  priority: 0,
  mask: [],
};

interface PhoneMaskItem {
  mask: string;
  startsWith: string;
}

const GENERIC_PHONE_MASK: PhoneMaskItem = {
  mask: '+000000000000000',
  startsWith: '',
};

@Component({
  selector: 'phone-v2',
  templateUrl: './phone-v2.component.html',
  styleUrls: ['./phone-v2.component.scss'],
  providers: [
    CountryCode,
    {
      provide: NG_VALUE_ACCESSOR,
      // tslint:disable-next-line:no-forward-ref
      useExisting: forwardRef(() => PhoneV2Component),
      multi: true,
    },
  ],
})
export class PhoneV2Component extends BasefieldV2Component implements OnInit {
  innerModel = new FormControl('');

  phoneUtil: any = lpn.PhoneNumberUtil.getInstance();

  selectedCountry: Country = DEFAULT_COUNTRY;

  imask: IMask.MaskedDynamicOptions;

  constructor(private countries: CountryCode) {
    super();
  }

  ngOnInit(): void {
    this.imask = this.buildImaskOptions();

    if (this.formControl.value) {
      this.setSelectedCountry(this.formControl.value as string);
      this.innerModel.setValue(this.formControl.value as string);
    }
  }

  public onPhoneNumberChange(): void {
    const val = this.innerModel.value as string;
    if (val === '') {
      this.selectedCountry = DEFAULT_COUNTRY;
    }

    this.setSelectedCountry(val);

    this.formControl.setValue(val);
    this.formControl.markAsDirty();
  }

  setSelectedCountry(val: string) {
    const number = this.getParsedNumber(val, '');
    const countryCode =
      number && number.getCountryCode()
        ? this.getCountryIsoCode(number.getCountryCode(), number)
        : this.selectedCountry.iso2;

    if (countryCode && countryCode !== this.selectedCountry.iso2) {
      const newCountry = this.countries.allCountries
        .sort((a, b) => {
          return a.priority - b.priority;
        })
        .find((c) => c.iso2 === countryCode);
      if (newCountry) {
        this.selectedCountry = {
          ...newCountry,
          priority: +newCountry.priority || 0,
          flagClass: `iti__${newCountry.iso2.toLocaleLowerCase()}`,
        };
      }
    }

    return countryCode;
  }

  private buildImaskOptions(): IMask.MaskedDynamicOptions {
    const countryMasks = this.countries.allCountries.flatMap((country) => {
      const prefixes = country.areaCodes
        ? country.areaCodes.map((areaCode) => `${country.dialCode}${areaCode}`)
        : [country.dialCode];

      return country.mask.flatMap((maskPattern) =>
        prefixes.map((startsWith) => ({
          mask: `+${maskPattern}`,
          startsWith,
        })),
      );
    });

    return {
      mask: [...countryMasks, GENERIC_PHONE_MASK],
      dispatch: (appended, dynamicMasked) => {
        const number = (dynamicMasked.value + appended).replace(/\D/g, '');
        const getPrefix = (m: IMask.AnyMasked) => (m as unknown as PhoneMaskItem).startsWith;
        const matching = dynamicMasked.compiledMasks
          .filter((m) => {
            const prefix = getPrefix(m);
            return prefix && number.indexOf(prefix) === 0;
          })
          .sort((a, b) => getPrefix(b).length - getPrefix(a).length);

        return matching[0] ?? dynamicMasked.compiledMasks[dynamicMasked.compiledMasks.length - 1];
      },
    };
  }

  private getParsedNumber(phoneNumber: string, countryCode: string): lpn.PhoneNumber | undefined {
    let number: lpn.PhoneNumber | undefined = undefined;
    try {
      number = this.phoneUtil.parse(phoneNumber, countryCode.toUpperCase());
      // eslint-disable-next-line no-empty
    } catch (e) {}
    return number;
  }

  /**
   * Sifts through all countries and returns iso code of the primary country
   * based on the number provided.
   * @param countryCode country code in number format
   * @param number PhoneNumber object
   */
  private getCountryIsoCode(countryCode: number | undefined, number: any): string | undefined {
    // Will use this to match area code from the first numbers
    const rawNumber = number['values_']['2'].toString();
    // List of all countries with countryCode (can be more than one. e.x. US, CA, DO, PR all have +1 countryCode)
    const countries = this.countries.allCountries.filter(
      (c) => c.dialCode === countryCode?.toString(),
    );
    // Main country is the country, which has no areaCodes specified in country-code.ts file.
    const mainCountry = countries.find((c) => c.areaCodes === null);
    // Secondary countries are all countries, which have areaCodes specified in country-code.ts file.
    const secondaryCountries = countries.filter((c) => c.areaCodes !== null);
    let matchedCountry = mainCountry ? mainCountry.iso2 : undefined;

    /*
      Iterate over each secondary country and check if nationalNumber starts with any of areaCodes available.
      If no matches found, fallback to the main country.
    */
    secondaryCountries.forEach((country) => {
      country.areaCodes?.forEach((areaCode) => {
        if (rawNumber.startsWith(areaCode)) {
          matchedCountry = country.iso2;
        }
      });
    });

    return matchedCountry;
  }
}
